import React, { PureComponent } from 'react';
import { Polygon, Circle, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import compose from 'recompose/compose';
import withStateHandlers from 'recompose/withStateHandlers';
import withHandlers from 'recompose/withHandlers';
import { List } from 'immutable';
import classnames from 'classnames';
import { translate } from 'react-i18next';

import ZoneDetailsPopup from 'common/components/zoneMap/ZoneDetailsPopupContainer';
import ZoneDeleteDialog from 'common/components/zoneMap/ZoneDeleteDialog';

import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';
import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';
import { LatLngInnerPolygon } from 'common/models/internal/LatLngPolygon';
import NumberUtils from 'common/utils/NumberUtils';

import theme from './Zone.scss';

const DEFAULT_STYLE = {
    weight: 2,
    fillOpacity: 0.2
};

const HIGHLIGHT_STYLE = {
    weight: 5,
    fillOpacity: 0.65
};
class Zone extends PureComponent {
    state = {
        targetLayer: null,
        isEditable: false
    };

    componentDidUpdate(prevProps) {
        const { targetLayer } = this.state;
        const {
            zone,
            selectedZoneId,
            isSelected,
            isHovered,
            zoneBounds,
            map,
            isEditing
        } = this.props;

        const { color } = zone.getStyles();
        //Focus on hover
        if (!prevProps.isHovered && isHovered && !isEditing) {
            this.focus();
        }

        if (
            !prevProps.isEditing &&
            isEditing &&
            zone.getInternalStatus() !== ZoneMapGeometryStatusEnum.New
        ) {
            this.popup = targetLayer.getPopup();
            targetLayer.unbindPopup();
        }

        if (prevProps.isEditing && !isEditing) {
            targetLayer.bindPopup(this.popup);
            this.popup = null;
        }

        //Unfocus on mouse leave if not selected
        if (!isSelected && prevProps.isHovered && !isHovered) {
            this.unfocus();
            selectedZoneId && targetLayer && targetLayer.bringToBack();
        }

        //Focus and set zone editable on select.
        if (
            !prevProps.isSelected &&
            isSelected &&
            zone &&
            map &&
            zoneBounds &&
            targetLayer &&
            !isEditing
        ) {
            const zoneLatLng = zoneBounds.getNorthEast();
            const mapBounds = map.getBounds();

            this.focus();
            if (mapBounds && !mapBounds.contains(zoneLatLng)) {
                map.panTo(zoneLatLng);
            }
            targetLayer.pm.enable({ draggable: false });
            if (!targetLayer.isPopupOpen()) {
                targetLayer.openPopup();
            }
        }

        //Also set popup editable if new zone
        if (
            zone.getInternalStatus() === ZoneMapGeometryStatusEnum.New &&
            isSelected
        ) {
            this.setZoneEditable();
        }

        if (
            (isSelected || isHovered) &&
            prevProps.zone.getStyles().color !== color
        ) {
            this.focus();
        }

        //Reset zone on unselect
        if (prevProps.isSelected && !isSelected) {
            this.unfocus();
            this.resetZoneEditable();
            targetLayer.pm.disable();
        }
    }

    focus = () => {
        const { targetLayer } = this.state;

        if (targetLayer) {
            targetLayer.setStyle(HIGHLIGHT_STYLE);
            targetLayer.bringToFront();
        }
    };

    unfocus = () => {
        const { targetLayer } = this.state;
        if (targetLayer) {
            targetLayer.setStyle(DEFAULT_STYLE);
        }
    };

    onClickHandler = () => {
        const { targetLayer } = this.state;
        const { zone, onClick } = this.props;
        onClick({ zone, layer: targetLayer });
    };

    onMouseOver = () => {
        const { zone, handleGeometryHover } = this.props;
        handleGeometryHover({ geometry: zone });
    };

    onMouseOut = () => {
        const { resetHoveredGeometries } = this.props;
        resetHoveredGeometries();
    };

    onDelete = () => {
        const { deleteZone } = this.props;
        deleteZone();
        this.resetZoneEditable();
    };

    renderDetailsPopup = () => {
        const {
            zone,
            teamId,
            folders,
            isSelected,
            handleZoneDelete,
            onUpdateGeometry,
            pincodes,
            enableMapEvents,
            disableMapEvents,
            setDeleteConfirmationDialogInactive,
            type,
            availableVehicles,
            availableVehicleModels,
            mapMetadataSettings
        } = this.props;
        const { isEditable } = this.state;
        return (
            <ZoneDetailsPopup
                teamId={teamId}
                pincodes={pincodes}
                active={isSelected}
                zone={zone}
                folders={folders}
                isEditable={isEditable}
                handleZoneDelete={handleZoneDelete}
                onUpdateGeometry={onUpdateGeometry}
                handlePopupClose={this.handlePopupClose}
                setZoneEditable={this.setZoneEditable}
                enableMapEvents={enableMapEvents}
                disableMapEvents={disableMapEvents}
                onDelete={this.onDelete}
                setDeleteConfirmationDialogInactive={
                    setDeleteConfirmationDialogInactive
                }
                zoneType={type}
                availableVehicles={availableVehicles}
                availableVehicleModels={availableVehicleModels}
                mapMetadataSettings={mapMetadataSettings}
            />
        );
    };

    renderTooltip = () => {
        const {
            zone: { name, routeId }
        } = this.props;
        return (
            <Tooltip permanent direction="center" className={theme.labelWrap}>
                <span className={theme.label}>
                    {`${name}${routeId ? ` - ${routeId}` : ''}`}
                </span>
            </Tooltip>
        );
    };

    renderCircleTooltip = () => {
        const {
            zone: {
                geometry: { circle: radius },
                name,
                routeId
            },
            namedZones,
            t
        } = this.props;
        return (
            <Tooltip
                permanent
                className={classnames(
                    theme.labelWrap,
                    theme.radiusTooltipContainer
                )}
                opacity={1}
                direction="center"
            >
                {namedZones && (
                    <div>{`${name}${routeId ? ` - ${routeId}` : ''}`}</div>
                )}
                <div className={theme.radiusLabel}>{`${NumberUtils.roundOff(
                    radius.radius
                )}${t('units.meter')}`}</div>
            </Tooltip>
        );
    };

    setZoneEditable = () => {
        const { map } = this.props;
        this.setState({ isEditable: true });
        map.scrollWheelZoom && map.scrollWheelZoom.disable();
        // Need explicit pop up update after editable pop up opens due to a hack implemented at -
        // app/jsx/components/core/leaflet/LeafletInternalPopup.jsx - line 86
        // Due to the hack, pane does not adjust automatically
        setTimeout(() => {
            if (map && map._popup) {
                map._popup.update();
            }
        }, 0);
    };

    resetZoneEditable = () => {
        const { map } = this.props;
        this.setState({ isEditable: false });
        map.scrollWheelZoom && map.scrollWheelZoom.enable();
    };

    handleZoneEdit = e => {
        const { onUpdateGeometry, zone, setLayerRef } = this.props;
        const target = e && e.target;
        switch (zone.getType()) {
            case ZoneMapGeometryTypeEnum.Polygon: {
                const [vertices, ...innerPolygonVertices] =
                    target && target.getLatLngs();
                const innerPolygons = List(innerPolygonVertices).map(polygon =>
                    LatLngInnerPolygon.fromJS({
                        vertices: polygon
                    })
                );

                let updatedZone = zone;
                if (vertices) {
                    updatedZone = updatedZone.updateVertices(vertices);

                    if (innerPolygons) {
                        updatedZone = updatedZone.setInnerPolygons(
                            innerPolygons
                        );
                    }

                    onUpdateGeometry(updatedZone);
                }
                break;
            }

            case ZoneMapGeometryTypeEnum.Polyline: {
                const vertices = target && target.getLatLngs();
                vertices && onUpdateGeometry(zone.updateVertices(vertices));
                break;
            }
            case ZoneMapGeometryTypeEnum.Circle: {
                const radius = target && target.getRadius();
                radius && onUpdateGeometry(zone.updateRadius(radius));
                break;
            }
        }
        this.setLayerRef(target, zone, setLayerRef);
    };

    handlePopupClose = () => {
        const { targetLayer } = this.state;
        const { zone, handlePopupClose } = this.props;
        targetLayer && targetLayer.closePopup();
        this.resetZoneEditable();
        handlePopupClose({ zone });
    };

    setLayerRef = (layer, geometry, callback) => {
        let bounds;
        if (layer) {
            bounds =
                geometry.getType() !== ZoneMapGeometryTypeEnum.Circle
                    ? layer.getBounds()
                    : L.latLngBounds([layer.getLatLng(), layer.getLatLng()]);
            callback({ geometry, layer, bounds });
            layer.setStyle(DEFAULT_STYLE);
        }
        this.setState({ targetLayer: layer });
    };

    layerRef = ref => {
        const { zone, setLayerRef } = this.props;
        const targetLayer = ref && ref.leafletElement;
        targetLayer && targetLayer.on('pm:edit', this.handleZoneEdit);
        this.setLayerRef(targetLayer, zone, setLayerRef);
    };

    renderLayer = () => {
        const { zone, type, namedZones } = this.props;
        if (!zone) {
            return null;
        }
        const { id } = zone;
        const { color, opacity } = zone.getStyles();
        switch (type) {
            case ZoneMapGeometryTypeEnum.Polygon: {
                let latLngs = zone
                    .getVertices()
                    .map(vertex => [vertex.lat, vertex.lng])
                    .toJS();
                const innerPolygons = zone.getInnerPolygons();

                if (innerPolygons && innerPolygons.size) {
                    const innerVertices = innerPolygons.map(polygon => {
                        const { vertices } = polygon;
                        return vertices.map(vertex => [vertex.lat, vertex.lng]);
                    });
                    latLngs = [latLngs, ...innerVertices.toJS()];
                }
                return (
                    <Polygon
                        id={id}
                        color={color}
                        opacity={opacity}
                        positions={latLngs}
                        onClick={this.onClickHandler}
                        onMouseOver={this.onMouseOver}
                        onMouseOut={this.onMouseOut}
                        ref={this.layerRef}
                    >
                        {this.renderDetailsPopup()}
                        {namedZones ? this.renderTooltip() : null}
                    </Polygon>
                );
            }

            case ZoneMapGeometryTypeEnum.Circle: {
                const { center, radius } = zone.geometry.circle;
                return (
                    <Circle
                        id={id}
                        color={color}
                        center={center}
                        radius={radius}
                        onClick={this.onClickHandler}
                        onMouseOver={this.onMouseOver}
                        onMouseOut={this.onMouseOut}
                        ref={this.layerRef}
                    >
                        {this.renderDetailsPopup()}
                        {this.renderCircleTooltip()}
                    </Circle>
                );
            }

            case ZoneMapGeometryTypeEnum.Polyline: {
                const latLngs = zone
                    .getVertices()
                    .map(vertex => [vertex.lat, vertex.lng])
                    .toJS();
                return (
                    <Polyline
                        id={id}
                        color={color}
                        opacity={opacity}
                        positions={latLngs}
                        onClick={this.onClickHandler}
                        onMouseOver={this.onMouseOver}
                        onMouseOut={this.onMouseOut}
                        ref={this.layerRef}
                    >
                        {this.renderDetailsPopup()}
                        {namedZones ? this.renderTooltip() : null}
                    </Polyline>
                );
            }
        }
    };

    render() {
        const {
            isDeleteConfirmationDialogActive,
            setDeleteConfirmationDialogInactive,
            onZoneDelete
        } = this.props;
        return (
            <div>
                {this.renderLayer()}
                {isDeleteConfirmationDialogActive ? (
                    <ZoneDeleteDialog
                        active={isDeleteConfirmationDialogActive}
                        onAccept={onZoneDelete}
                        onCancel={setDeleteConfirmationDialogInactive}
                    />
                ) : null}
            </div>
        );
    }
}

const ControlledZone = compose(
    withStateHandlers(
        () => ({
            isDeleteConfirmationDialogActive: false
        }),
        {
            setDeleteConfirmationDialogActive: () => () => {
                return {
                    isDeleteConfirmationDialogActive: true
                };
            },
            setDeleteConfirmationDialogInactive: () => () => {
                return {
                    isDeleteConfirmationDialogActive: false
                };
            }
        }
    ),
    withHandlers({
        onZoneDelete: ({
            zone: { id: zoneId },
            handleZoneDelete,
            setDeleteConfirmationDialogInactive
        }) => () => {
            handleZoneDelete({ zoneId });
            setDeleteConfirmationDialogInactive();
        }
    }),
    withHandlers({
        deleteZone: ({
            setDeleteConfirmationDialogActive,
            onZoneDelete,
            zone
        }) => () => {
            zone && zone.isVerified()
                ? setDeleteConfirmationDialogActive()
                : onZoneDelete();
        }
    }),
    translate('translations')
)(Zone);

export default ControlledZone;
